import { cn } from '@/lib/utils';

interface PulsingOrbLoaderProps {
  label?: string;
  sublabel?: string;
  fullScreen?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Premium pulsing orb loader — concentric pulse rings around a soft gradient orb.
 * Matches the visual language of RouteLoader. Use for any page-level loading state.
 */
export function PulsingOrbLoader({
  label,
  sublabel,
  fullScreen = false,
  className,
  size = 'md',
}: PulsingOrbLoaderProps) {
  const dim =
    size === 'sm' ? 'h-10 w-10' : size === 'lg' ? 'h-16 w-16' : 'h-14 w-14';
  const orb =
    size === 'sm' ? 'h-2.5 w-2.5' : size === 'lg' ? 'h-3.5 w-3.5' : 'h-3 w-3';

  const content = (
    <div className={cn('flex flex-col items-center justify-center', className)}>
      <div className={cn('relative flex items-center justify-center', dim)}>
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border border-primary/30 animate-route-ring [animation-delay:0ms]"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border border-primary/20 animate-route-ring [animation-delay:600ms]"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border border-primary/10 animate-route-ring [animation-delay:1200ms]"
        />
        <span
          aria-hidden="true"
          className={cn(
            'relative rounded-full bg-gradient-to-br from-primary to-primary/60 shadow-[0_0_20px_hsl(var(--primary)/0.55)] animate-route-orb',
            orb
          )}
        />
      </div>
      {label && (
        <p className="mt-5 text-[14px] font-medium text-foreground text-center">
          {label}
        </p>
      )}
      {sublabel && (
        <p className="mt-1 text-[12px] text-muted-foreground text-center max-w-[34ch]">
          {sublabel}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-background px-6"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {content}
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" aria-busy="true">
      {content}
    </div>
  );
}
